import React, { memo, SyntheticEvent, useEffect, useState } from 'react';
import FloatingMessage from '../floatingMessage/floatingMessage';
import './multiSelector.scss';
import { InlineTypeAhead } from '../inlineTypeAhead/inlineTypeAhead';
import Button from '../button/button';

export interface MultiSelectorProps {
    setParentTags: (keywords: string[]) => void;
    submitTypeAhead?: () => void;
    inputClass: string;
    isTypeAhead: boolean;
    options?: string[];
    defaultSelected?: string[];
    label?: string;
    clear?: boolean;
}

export interface MultiSelectorState {
    activeTags: string[];
    newTag: string;
    errorMessage: string;
}

const MultiSelector = ({ label, setParentTags, submitTypeAhead, isTypeAhead, options, inputClass, defaultSelected, clear = false }: MultiSelectorProps) => {

    const [state, setState] = useState<MultiSelectorState>({
        activeTags: [],
        newTag: '',
        errorMessage: '',
    });

    useEffect(() => {
        const defaultTags = defaultSelected && defaultSelected.length > 0 ? [...defaultSelected] : [];
        setState({ ...state, activeTags: defaultTags });
    }, [clear]);

    const handleNewTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const newTagText: string = e.target.value;
        setState({ ...state, newTag: newTagText });
    };

    const handleAddTag = (e: SyntheticEvent) => {
        e.preventDefault();
        const tagToAdd = state.newTag;
        if (state.activeTags.includes(tagToAdd)) {
            setState({ ...state, errorMessage: 'Duplicate tag' });
        }
        else if (tagToAdd.length > 0) {
            updateTags(tagToAdd);
        } else {
            setState({ ...state, errorMessage: '', newTag: '' });
        }
    };

    const handleAddTagFromInlineTypeAhead = (newTag: string) => {
        if (state.activeTags.includes(newTag)) {
            setState({ ...state, errorMessage: 'Duplicate tag' });
        }
        else if (newTag.length > 0) {
            updateTags(newTag);
        } else {
            setState({ ...state, errorMessage: '', newTag: '' });
        }
    };

    const updateTags = (tagToAdd: string) => {
        setParentTags([...state.activeTags, tagToAdd]);
        setState({ ...state, activeTags: [...state.activeTags, tagToAdd], errorMessage: '', newTag: '' });
    };

    const handleRemoveTag = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const value = (e.target as HTMLInputElement).value;
        const allTags = state.activeTags;
        allTags.splice(allTags.indexOf(value), 1);
        setState({ ...state, activeTags: [...allTags] });

        setParentTags([...allTags]);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleAddTag(e);
        }
    };

    return (
        <div className="multiselector is-hidden has-typeahead has-freetype" data-testid="multiselector">
            <div className="multiselector-selected">
                {state.activeTags.map((tag: string) => (
                    <span key={tag} data-testid={`active-tag-${tag}`} className="active-tag">
                        <input tabIndex={0} className="selected" type="hidden" value={tag} />
                        {tag}
                        <button className="plain-link remove-button error-text" value={tag} onClick={handleRemoveTag}></button>
                    </span>
                ))
                }
            </div>
            <div className="multiselector-input-field" data-testid="multiselect-input-field">
                {label && <label className="input-label" style={{ fontSize: '15px' }}>{label}:</label>}
                {isTypeAhead ?
                    <div className="multiselect-typeahead">
                        <InlineTypeAhead options={options || []} alwaysShowOptions={false} setValue={handleAddTagFromInlineTypeAhead} />
                        <div className="center-button">
                            <Button variant="button button--small" onClick={submitTypeAhead}>Submit</Button>
                        </div>
                    </div>
                    :
                    <>
                        <input type="text" style={{ paddingRight: '2em' }} className={inputClass} aria-label={label} value={state.newTag} onChange={handleNewTagChange} onKeyDown={handleKeyDown} data-testid="multiselect-input" />
                        <button className="button freetype-add-button plain-link" onClick={handleAddTag} data-testid="multiselect-enter-button">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlSpace="preserve" height="8" viewBox="0 0 50 50"><path d="M25 .5 6.4 19.1c-2.3 2.3-2.3 5.9 0 8.2s5.9 2.3 8.2 0l4.6-4.6v21c0 3.2 2.6 5.8 5.8 5.8 3.2 0 5.8-2.6 5.8-5.8v-21l4.6 4.6c1.1 1.1 2.6 1.7 4.1 1.7 1.5 0 3-.6 4.1-1.7 2.3-2.3 2.3-5.9 0-8.2L25 .5z" /></svg>
                        </button>
                    </>
                }

            </div>
            {state.errorMessage && <FloatingMessage message={state.errorMessage} variant="error-box" />}
        </div>
    );
};

export default memo(MultiSelector);
